/**
 * announcerFx — broadcast-grade voice processing for live mic.
 *
 * What this does (and why)
 * ------------------------
 * Chains a few WebAudio nodes together to give a host's voice that
 * "polished broadcast" feel without breaking the browser's built-in
 * echo cancellation:
 *
 *   1. HighPassFilter @ 80Hz   → kills HVAC rumble, fan noise, mic-stand
 *      handling, room AC. Big perceptual win for free.
 *   2. PeakingEQ @ 200Hz -2dB  → de-mud the chest-resonance frequency.
 *   3. PeakingEQ @ 3kHz +3dB   → "presence" boost for clarity / intelligibility.
 *   4. DynamicsCompressor      → gentle 3:1 ratio at -18dB → keeps quiet
 *      asides audible without making loud peaks distort.
 *   5. Makeup gain +3dB        → recover the level the compressor pulled.
 *
 * Returns a NEW MediaStream whose audio track is the processed signal.
 * The caller swaps this track into the peer connection in place of the
 * raw mic. The original raw track stays alive on the source stream so we
 * can A/B (toggle FX off without reacquiring the camera).
 *
 * IMPORTANT: this MUST be applied to a stream where audio was captured
 * mono (channelCount: 1). Stereo capture disables AEC; processing the
 * stereo track here would still leave the echo. See useMediaStream.js.
 */

let _ctx = null;

function ctx() {
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: "interactive" });
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

/**
 * Apply Announcer FX to a MediaStream.
 *
 * @param {MediaStream} sourceStream  raw mic stream (mono recommended)
 * @returns {{ stream: MediaStream, dispose: () => void }}
 */
export function applyAnnouncerFx(sourceStream) {
  const ac = ctx();
  const src = ac.createMediaStreamSource(sourceStream);

  const hp = ac.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 80;
  hp.Q.value = 0.707;

  const cutMud = ac.createBiquadFilter();
  cutMud.type = "peaking";
  cutMud.frequency.value = 200;
  cutMud.Q.value = 1;
  cutMud.gain.value = -2;

  const presence = ac.createBiquadFilter();
  presence.type = "peaking";
  presence.frequency.value = 3000;
  presence.Q.value = 1;
  presence.gain.value = 3;

  const comp = ac.createDynamicsCompressor();
  comp.threshold.value = -18;
  comp.knee.value = 12;
  comp.ratio.value = 3;
  comp.attack.value = 0.005;
  comp.release.value = 0.12;

  const makeup = ac.createGain();
  makeup.gain.value = 1.4; // ≈ +3dB

  const dest = ac.createMediaStreamDestination();

  src.connect(hp);
  hp.connect(cutMud);
  cutMud.connect(presence);
  presence.connect(comp);
  comp.connect(makeup);
  makeup.connect(dest);

  return {
    stream: dest.stream,
    dispose: () => {
      try { src.disconnect(); hp.disconnect(); cutMud.disconnect(); presence.disconnect(); comp.disconnect(); makeup.disconnect(); } catch {}
    },
  };
}
